/**
 * Error handling for 401 Unauthorized errors
 * Integrates with router context to update auth state
 *
 * Note: Main 401 handling is done in customFetch via router.invalidate(),
 * which triggers beforeLoad to refresh tokens through initAuth.
 * This module provides fallback handling via QueryClient subscriptions.
 */

import type { QueryClient } from "@tanstack/react-query"
import {
	clearAllAuthData,
	refreshAuthTokens,
	saveOAuthTokens,
} from "@/shared/lib/auth"

interface ErrorWithStatus {
	status?: number
	message?: string
}

function isErrorWithStatus(error: unknown): error is ErrorWithStatus {
	return (
		error !== null &&
		error !== undefined &&
		typeof error === "object" &&
		"status" in error
	)
}

export function isUnauthorizedError(error: unknown): boolean {
	if (!isErrorWithStatus(error)) {
		return false
	}
	return error.status === 401
}

const handleRefreshAuthTokens = async () => {
	const data = await refreshAuthTokens()
	if (data) {
		saveOAuthTokens({
			token: data?.access_token || "",
			refreshToken: data?.refresh_token || "",
		})
	} else {
		console.error("Error while refreshing auth tokens")
		clearAllAuthData()
		window.location.reload()
	}
}

/**
 * Setup error handling for 401 Unauthorized errors
 * Subscribes to QueryClient mutation cache to catch 401 errors
 *
 * on 401 error refreshes tokens -> sets new token in cookie. If error occurs during refresh,
 * deletes tokens from localstorage and reloads page
 */
export function setupAuthErrorHandling(queryClient: QueryClient): () => void {
	const unsubscribeMutations = queryClient
		.getMutationCache()
		.subscribe((event) => {
			if (
				event.type === "updated" &&
				"mutation" in event &&
				event.mutation.state.status === "error"
			) {
				const error = event.mutation.state.error
				if (isUnauthorizedError(error)) {
					handleRefreshAuthTokens().finally(() => {
						queryClient.invalidateQueries()
					})
				}
			}
		})

	const unsubscribeQueries = queryClient.getQueryCache().subscribe((event) => {
		if (
			event.type === "updated" &&
			"query" in event &&
			event.query.state.status === "error"
		) {
			const error = event.query.state.error
			if (isUnauthorizedError(error)) {
				handleRefreshAuthTokens().finally(() => {
					queryClient.invalidateQueries()
				})
			}
		}
	})

	return () => {
		unsubscribeMutations()
		unsubscribeQueries()
	}
}
